import { Box } from '@mui/material';

/**
 * Wraps a page's toolbar (search, filters, action buttons) with responsive behavior.
 *
 * Desktop: horizontal flex row with wrapping
 * Mobile: 2-column grid for compact inputs, full-width for search/autocomplete
 *
 * Usage:
 *   <ResponsiveToolbar>
 *     <Autocomplete ... />     // full width on mobile
 *     <TextField select ... /> // half width on mobile
 *     <DatePicker ... />       // half width on mobile
 *     <Button ... />           // half width on mobile
 *   </ResponsiveToolbar>
 */
const ResponsiveToolbar = ({ children, sx, ...props }) => {
  return (
    <Box
      sx={{
        display: { xs: 'grid', md: 'flex' },
        gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'none' },
        overflow: { xs: 'hidden', md: 'visible' },
        flexWrap: 'wrap',
        gap: { xs: 0.75, md: 2 },
        alignItems: { xs: 'stretch', md: 'center' },
        mb: 2,
        // On mobile, Autocomplete spans full width (2 columns)
        '& > .MuiAutocomplete-root': {
          gridColumn: { xs: '1 / -1', md: 'auto' },
        },
        // Mobile-only: compact inputs and tooltip-wrapped button span fix
        '@media (max-width: 600px)': {
          // Tooltips wrapping buttons need display:flex so grid column spanning works
          '& > span': {
            display: 'flex',
          },
          // Compact input value font (don't override padding — that breaks the floating label)
          '& .MuiInputBase-input': {
            fontSize: '0.85rem',
          },
          // Shrink the floating label slightly so it fits inside the size="small" outlined border
          '& .MuiInputLabel-root': {
            fontSize: '0.85rem',
          },
          '& .MuiInputAdornment-root .MuiIconButton-root': {
            padding: '4px',
          },
          '& .MuiButton-root': {
            fontSize: '0.85rem',
          },
        },
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

/**
 * Wraps action buttons section within a toolbar.
 * Desktop: inline with other toolbar items
 * Mobile: full-width row with buttons side-by-side
 */
export const ToolbarActions = ({ children, sx, ...props }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        flexWrap: 'wrap',
        alignItems: 'center',
        gridColumn: { xs: '1 / -1', md: 'auto' },
        '& > .MuiButton-root': {
          flex: { xs: 1, md: 'none' },
          minWidth: { xs: 0, md: 'auto' },
        },
        ...sx,
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

export default ResponsiveToolbar;
